import { TextField, Tooltip, Typography, makeStyles } from '@material-ui/core'
import React from 'react'

// Counting a bundle of notes, and showing what a counted bundle came to.
//
// Used in three places that are the same act: the centre counting a collection
// into an envelope, HQ counting the same envelope again when it arrives, and
// HQ writing out a pay-in slip. A bank asks how many of each note it is being
// handed, never for a total — so this component's total is always computed
// from the nine boxes and never typed anywhere.
//
// ⭐ The denominations come from the server (`denominations` in the ledger and
// safe payloads). Nothing here keeps its own list: the day a ₹1,000 note comes
// back, that is a backend constant and a migration, not a frontend release.
const FALLBACK_DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1]

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    // A row needs 46 (face) + 62 (input) + ~64 (computed value) + gaps ≈ 196px.
    // A narrower track clips the value — "₹38,0(" instead of "₹38,000" — which
    // is worse than useless on a screen whose whole job is arithmetic.
    gridTemplateColumns: 'repeat(auto-fit, minmax(196px, 1fr))',
    gap: theme.spacing(0.75),
    // The counter always sits in its own full-width row, never inside a table
    // cell — an input wider than its column paints over the cells beside it.
    // Three across, three down: the shape of a counting sheet.
    maxWidth: 620
  },
  row: { display: 'flex', alignItems: 'center', gap: 6 },
  face: {
    fontVariantNumeric: 'tabular-nums',
    fontSize: 12,
    fontWeight: 600,
    color: '#3f5b96',
    background: '#eaf0fa',
    borderRadius: 3,
    padding: '3px 0',
    textAlign: 'center',
    width: 46,
    flex: 'none'
  },
  times: { color: '#9e9e9e', fontSize: 11, flex: 'none' },
  input: { width: 62, flex: 'none' },
  value: {
    fontVariantNumeric: 'tabular-nums',
    fontSize: 12,
    color: '#757575',
    minWidth: 58,
    whiteSpace: 'nowrap'
  },
  faint: { opacity: 0.35 },
  totals: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(3),
    marginTop: theme.spacing(1.25),
    paddingTop: theme.spacing(1.25),
    borderTop: '1px dashed #d5d5d5'
  },
  total: { fontVariantNumeric: 'tabular-nums', fontSize: 20, fontWeight: 600 },
  ok: { color: '#1b6e52' },
  off: { color: '#a32b21' },
  cap: {
    fontSize: 10.5,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: '#8a8a8a',
    fontWeight: 700
  },
  tally: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) },
  pill: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: '3px 8px',
    fontVariantNumeric: 'tabular-nums',
    fontSize: 12
  },
  pillFace: { color: '#3f5b96', fontWeight: 600 },
  pillNil: { opacity: 0.4 },
  pillNeg: { borderColor: '#e8b4ae', background: '#fbeeec', color: '#a32b21' }
}))

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

// Counts travel as {"500": 12} — the API's own shape, so nothing has to be
// translated on the way in or out.
const noteTotal = (counts, denominations = FALLBACK_DENOMINATIONS) =>
  denominations.reduce((sum, d) => {
    const n = parseInt(String(counts?.[d] ?? counts?.[String(d)] ?? 0), 10)
    return sum + (Number.isNaN(n) || n < 0 ? 0 : n * d)
  }, 0)

const anyCounted = (counts, denominations = FALLBACK_DENOMINATIONS) =>
  denominations.some((d) => {
    const n = parseInt(String(counts?.[d] ?? counts?.[String(d)] ?? 0), 10)
    return !Number.isNaN(n) && n > 0
  })

// A count that came back from the server, ready to edit: blanks instead of
// zeroes, so eight empty boxes don't read as eight counted nothings.
const toEditable = (counts, denominations = FALLBACK_DENOMINATIONS) => {
  const out = {}
  denominations.forEach((d) => {
    const n = Number(counts?.[String(d)] ?? counts?.[d] ?? 0)
    out[String(d)] = n ? String(n) : ''
  })
  return out
}

// Back to the API's shape. Blank means none.
const toPayload = (counts, denominations = FALLBACK_DENOMINATIONS) => {
  const out = {}
  denominations.forEach((d) => {
    const n = parseInt(String(counts?.[String(d)] ?? ''), 10)
    out[String(d)] = Number.isNaN(n) || n < 0 ? 0 : n
  })
  return out
}

/**
 * The nine boxes. `target` is the figure the count is supposed to come to —
 * pass it and the difference is called out live, which is the whole point of
 * counting rather than typing.
 */
const NoteCounter = ({
  denominations = FALLBACK_DENOMINATIONS,
  counts,
  onChange,
  disabled = false,
  target = null,
  targetLabel = 'the amount entered',
  totalLabel = 'Counted'
}) => {
  const classes = useStyles()
  const total = noteTotal(counts, denominations)
  const hasTarget = target !== null && target !== undefined && target !== ''
  const difference = hasTarget ? total - Number(target) : 0
  const counted = anyCounted(counts, denominations)

  const set = (d, raw) => {
    // Digits only — a stray minus or decimal in a note count is never a real
    // intention, and the server rejects it anyway.
    const clean = String(raw)
      .replace(/[^0-9]/g, '')
      .slice(0, 5)
    onChange({ ...counts, [String(d)]: clean })
  }

  return (
    <div>
      <div className={classes.grid}>
        {denominations.map((d) => {
          const raw = counts?.[String(d)] ?? ''
          const n = parseInt(String(raw), 10)
          const value = Number.isNaN(n) ? 0 : n * d
          return (
            <div className={classes.row} key={d}>
              <span className={classes.face}>₹{d}</span>
              <span className={classes.times}>×</span>
              <TextField
                size="small"
                variant="outlined"
                className={classes.input}
                value={raw}
                disabled={disabled}
                inputProps={{
                  inputMode: 'numeric',
                  style: { textAlign: 'right', padding: '6px 8px' },
                  'aria-label': `Number of ${d} rupee notes`
                }}
                onChange={(e) => set(d, e.target.value)}
              />
              <span
                className={`${classes.value} ${value ? '' : classes.faint}`}
              >
                {value ? inr(value) : '—'}
              </span>
            </div>
          )
        })}
      </div>

      <div className={classes.totals}>
        <div>
          <div className={classes.cap}>{totalLabel}</div>
          <div className={classes.total}>{inr(total)}</div>
        </div>
        {hasTarget && counted && (
          <div>
            <div className={classes.cap}>Against {targetLabel}</div>
            <div
              className={`${classes.total} ${
                difference === 0 ? classes.ok : classes.off
              }`}
            >
              {difference === 0
                ? 'Tallies'
                : `${difference > 0 ? '+' : '−'}${inr(Math.abs(difference))}`}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// A per-denomination map ({"500": 12}) as ordered tally rows.
//
// ⭐ Never iterate such a map with Object.keys: JavaScript orders integer-like
// keys ASCENDING, so the pills come out ₹1 first and a bundle of 76 five-
// hundreds reads as "₹2 × 6". The server's `denominations` list is the order
// a person counts in, and it is the only order these may be rendered in.
const tallyRows = (counts, denominations = FALLBACK_DENOMINATIONS) =>
  denominations.map((d) => {
    const n = Number(counts?.[String(d)] ?? counts?.[d] ?? 0)
    return { denomination: d, count: n, value: d * n }
  })

/**
 * A counted bundle in one line — "76×₹500 +3 more", with the full count on
 * hover. For table cells, where nine stacked pills make the row unreadable.
 */
const NoteSummary = ({ rows = [], empty = '—' }) => {
  const counted = rows.filter((r) => r.count !== 0)
  if (!counted.length) return <span>{empty}</span>
  const [first, ...rest] = counted
  return (
    <Tooltip
      title={counted
        .map((r) => `${r.count} × ₹${r.denomination}`)
        .join('   ·   ')}
    >
      <span
        style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {first.count}×₹{first.denomination}
        {rest.length ? ` +${rest.length} more` : ''}
      </span>
    </Tooltip>
  )
}

/**
 * A counted bundle, read-only — what a centre is holding, what is in the HQ
 * drawer, what went into one pay-in slip. `rows` is the API's `note_tally`
 * shape: [{denomination, count, value}].
 */
const NoteTally = ({ rows = [], showZero = false }) => {
  const classes = useStyles()
  const shown = showZero ? rows : rows.filter((r) => r.count !== 0)
  if (!shown.length) {
    return (
      <Typography variant="caption" color="textSecondary">
        Nothing counted yet.
      </Typography>
    )
  }
  return (
    <div className={classes.tally}>
      {shown.map((r) => (
        <Tooltip key={r.denomination} title={inr(r.value)}>
          <span
            className={`${classes.pill} ${
              r.count === 0 ? classes.pillNil : ''
            } ${r.count < 0 ? classes.pillNeg : ''}`}
          >
            <span className={classes.pillFace}>₹{r.denomination}</span>
            <span>× {r.count}</span>
          </span>
        </Tooltip>
      ))}
    </div>
  )
}

export default NoteCounter
export {
  NoteSummary,
  NoteTally,
  anyCounted,
  noteTotal,
  tallyRows,
  toEditable,
  toPayload
}
